import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity, AlertTriangle, ArrowLeft, ArrowRight, Camera, Check,
  CheckCircle, ChevronRight, Cog, GraduationCap, HelpCircle, Loader2,
  Monitor, Play, RefreshCw, Server, Shield, ShieldCheck, Video,
  Wifi, XCircle, Zap
} from 'lucide-react';
import { toast } from 'react-toastify';
import fetchWithTenant from '../utils/fetchWithTenant';
import RaLicenseBanner from '../components/RaLicenseBanner';
import '../styles/v2/pages/recording-assurance-wizard.css';

const STEPS = [
  { id: 'welcome', title: 'Welcome', icon: Shield },
  { id: 'select-vms', title: 'Select VMS', icon: Server },
  { id: 'test-connection', title: 'Test Connection', icon: Wifi },
  { id: 'select-cameras', title: 'Select Cameras', icon: Camera },
  { id: 'configure', title: 'Configure', icon: Cog },
  { id: 'complete', title: 'Complete', icon: CheckCircle },
];

const RecordingAssuranceWizard = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [integrations, setIntegrations] = useState([]);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [connectionTest, setConnectionTest] = useState(null);
  const [testingConnection, setTestingConnection] = useState(false);
  const [cameras, setCameras] = useState([]);
  const [selectedCameras, setSelectedCameras] = useState(new Set());
  const [loadingCameras, setLoadingCameras] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [enrollResult, setEnrollResult] = useState(null);
  const [license, setLicense] = useState(null);

  // Load VMS integrations on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await fetchWithTenant('/api/integrations');
        if (res.ok) {
          const data = await res.json();
          const list = data.integrations || data || []; const vmsList = list.filter(i =>
            i.enabled && (i.type === 'WAVE_VMS' || i.type === 'GENETEC_VMS' || i.type === 'MILESTONE_VMS' || i.type === 'MIRASYS_VMS')
          );
          setIntegrations(vmsList);
        }
      } catch (err) {
        console.error('[RA Wizard] Load integrations error:', err);
      }

      // Load license
      try {
        const res = await fetchWithTenant('/api/recording-assurance/license');
        if (res.ok) {
          const data = await res.json();
          setLicense(data.license);
        }
      } catch (err) {
        console.error('[RA Wizard] Load license error:', err);
      }
    })();
  }, []);

  const testConnection = async () => {
    if (!selectedIntegration) return;
    setTestingConnection(true);
    setConnectionTest(null);
    try {
      const res = await fetchWithTenant(`/api/integrations/${selectedIntegration.id}/test`, { method: 'POST' });
      const data = await res.json();
      setConnectionTest(data);
      if (data.success) {
        toast.success('Connection successful');
      } else {
        toast.error('Connection failed: ' + (data.error || data.message));
      }
    } catch (err) {
      setConnectionTest({ success: false, error: err.message });
      toast.error('Connection test failed');
    }
    setTestingConnection(false);
  };

  const loadCameras = async () => {
    if (!selectedIntegration) return;
    setLoadingCameras(true);
    try {
      const res = await fetchWithTenant(`/api/vms/integrations/${selectedIntegration.id}/cameras`);
      if (res.ok) {
        const data = await res.json();
        setCameras(data.cameras || data || []);
        // Select all by default
        setSelectedCameras(new Set((data.cameras || data || []).map(c => c.id)));
      }
    } catch (err) {
      console.error('[RA Wizard] Load cameras error:', err);
      toast.error('Failed to load cameras');
    }
    setLoadingCameras(false);
  };

  const toggleCamera = (id) => {
    setSelectedCameras(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelectedCameras(new Set(cameras.map(c => c.id)));
  const selectNone = () => setSelectedCameras(new Set());

  const enrollCameras = async () => {
    if (!selectedIntegration || selectedCameras.size === 0) return;
    setEnrolling(true);
    try {
      const res = await fetchWithTenant('/api/recording-assurance/config/enable', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          integrationId: selectedIntegration.id,
          cameraIds: Array.from(selectedCameras)
        })
      });
      const data = await res.json();
      setEnrollResult(data);
      if (data.enabled > 0) {
        toast.success(`${data.enabled} cameras enabled for Recording Assurance`);
        setCurrentStep(5); // Go to complete
      } else {
        toast.error('No cameras were enabled');
      }
    } catch (err) {
      toast.error('Enrollment failed: ' + err.message);
    }
    setEnrolling(false);
  };

  const goNext = () => {
    if (currentStep === 1 && selectedIntegration) {
      testConnection();
    }
    if (currentStep === 2 && connectionTest?.success) {
      loadCameras();
    }
    setCurrentStep(s => Math.min(s + 1, STEPS.length - 1));
  };

  const goBack = () => setCurrentStep(s => Math.max(s - 1, 0));

  const canProceed = () => {
    switch (currentStep) {
      case 0: return true;
      case 1: return selectedIntegration !== null;
      case 2: return connectionTest?.success === true;
      case 3: return selectedCameras.size > 0;
      case 4: return true;
      default: return false;
    }
  };

  return (
    <div className="ra-wizard-page">
      <RaLicenseBanner />
      {/* Progress Steps */}
      <div className="ra-wizard-progress">
        {STEPS.map((step, i) => {
          const StepIcon = step.icon;
          const isActive = i === currentStep;
          const isComplete = i < currentStep;
          return (
            <div key={step.id} className={`ra-wizard-step ${isActive ? 'active' : ''} ${isComplete ? 'complete' : ''}`}>
              <div className="ra-wizard-step-icon">
                {isComplete ? <Check size={16} /> : <StepIcon size={16} />}
              </div>
              <span className="ra-wizard-step-label">{step.title}</span>
              {i < STEPS.length - 1 && <div className="ra-wizard-step-line" />}
            </div>
          );
        })}
      </div>

      {/* Step Content */}
      <div className="ra-wizard-content">
        {/* Step 0: Welcome */}
        {currentStep === 0 && (
          <div className="ra-wizard-panel">
            <div className="ra-wizard-hero">
              <Shield size={48} className="ra-wizard-hero-icon" />
              <h1>Recording Assurance Setup</h1>
              <p>Verify that your cameras are actually recording, not just streaming.</p>
            </div>

            <div className="ra-wizard-features">
              <div className="ra-wizard-feature">
                <div className="ra-wizard-feature-icon"><Activity size={24} /></div>
                <div>
                  <h3>Continuous Verification</h3>
                  <p>Every 10 minutes, the system checks each camera's recording status using three independent verification methods.</p>
                </div>
              </div>
              <div className="ra-wizard-feature">
                <div className="ra-wizard-feature-icon"><GraduationCap size={24} /></div>
                <div>
                  <h3>Intelligent Learning</h3>
                  <p>Over 7 days, the system learns each camera's normal behavior pattern. After that, it detects anomalies specific to each camera's schedule.</p>
                </div>
              </div>
              <div className="ra-wizard-feature">
                <div className="ra-wizard-feature-icon"><Zap size={24} /></div>
                <div>
                  <h3>Root Cause Analysis</h3>
                  <p>When a recording issue is detected, the system automatically identifies whether the problem is at the camera, server, or network level.</p>
                </div>
              </div>
              <div className="ra-wizard-feature">
                <div className="ra-wizard-feature-icon"><Server size={24} /></div>
                <div>
                  <h3>Works With Any VMS</h3>
                  <p>Supports Hanwha Wave, Genetec Security Center, and more. One dashboard for all your cameras regardless of vendor.</p>
                </div>
              </div>
            </div>

            {license && (
              <div className="ra-wizard-license-info">
                <span className={`ra-wizard-license-badge ${license.status?.toLowerCase()}`}>
                  {license.status === 'TRIAL' ? `Trial: ${license.trialDaysRemaining} days remaining` :
                   license.status === 'ACTIVE' ? license.skuLabel : license.status}
                </span>
                <span className="ra-wizard-license-cameras">
                  {license.currentCameras}/{license.maxCameras} cameras used
                </span>
              </div>
            )}
          </div>
        )}

        {/* Step 1: Select VMS */}
        {currentStep === 1 && (
          <div className="ra-wizard-panel">
            <h2>Select a Video Management System</h2>
            <p className="ra-wizard-desc">Choose which VMS you want to enable Recording Assurance for. You can add more later.</p>

            {integrations.length === 0 ? (
              <div className="ra-wizard-empty">
                <Server size={32} />
                <p>No VMS integrations found. Add a VMS integration in Settings first.</p>
                <button className="btn btn-secondary btn-sm" onClick={() => navigate('/admin')}>
                  Go to Settings
                </button>
              </div>
            ) : (
              <div className="ra-wizard-vms-grid">
                {integrations.map(int => (
                  <div
                    key={int.id}
                    className={`ra-wizard-vms-card ${selectedIntegration?.id === int.id ? 'selected' : ''}`}
                    onClick={() => setSelectedIntegration(int)}
                  >
                    <div className="ra-wizard-vms-header">
                      <Server size={20} />
                      <span className="ra-wizard-vms-type">{int.type.replace('_VMS', '').replace('_', ' ')}</span>
                    </div>
                    <h3>{int.name}</h3>
                    <span className="ra-wizard-vms-host">{int.config?.host || 'Agent tunnel'}</span>
                    {selectedIntegration?.id === int.id && (
                      <div className="ra-wizard-vms-check"><CheckCircle size={20} /></div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 2: Test Connection */}
        {currentStep === 2 && (
          <div className="ra-wizard-panel">
            <h2>Testing Connection</h2>
            <p className="ra-wizard-desc">Verifying connectivity to {selectedIntegration?.name}</p>

            <div className="ra-wizard-test-area">
              {testingConnection ? (
                <div className="ra-wizard-testing">
                  <Loader2 size={40} className="spin" />
                  <p>Connecting to {selectedIntegration?.name}...</p>
                  <span className="ra-wizard-testing-sub">Testing VMS API access</span>
                </div>
              ) : connectionTest ? (
                <div className={`ra-wizard-test-result ${connectionTest.success ? 'success' : 'error'}`}>
                  {connectionTest.success ? (
                    <>
                      <CheckCircle size={48} />
                      <h3>Connection Successful</h3>
                      <p>{connectionTest.message || 'Successfully connected to VMS'}</p>
                      {connectionTest.serverInfo && (
                        <div className="ra-wizard-test-details">
                          <span><Server size={14} /> {connectionTest.serverInfo.name}</span>
                          <span><Camera size={14} /> {connectionTest.serverInfo.cameraCount} cameras found</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <XCircle size={48} />
                      <h3>Connection Failed</h3>
                      <p>{connectionTest.error || connectionTest.message || 'Unable to reach VMS'}</p>
                      <button className="btn btn-secondary btn-sm" onClick={testConnection}>
                        <RefreshCw size={14} /> Retry
                      </button>
                    </>
                  )}
                </div>
              ) : (
                <div className="ra-wizard-testing">
                  <Wifi size={40} />
                  <p>Ready to test connection</p>
                  <button className="btn btn-primary btn-sm" onClick={testConnection}>
                    <Play size={14} /> Test Now
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Select Cameras */}
        {currentStep === 3 && (
          <div className="ra-wizard-panel">
            <h2>Select Cameras to Monitor</h2>
            <p className="ra-wizard-desc">
              Choose which cameras Recording Assurance should verify.
              {license && ` Your license allows up to ${license.maxCameras} cameras.`}
            </p>

            {loadingCameras ? (
              <div className="ra-wizard-testing">
                <Loader2 size={32} className="spin" />
                <p>Loading cameras from {selectedIntegration?.name}...</p>
              </div>
            ) : (
              <>
                <div className="ra-wizard-camera-controls">
                  <button className="btn btn-ghost btn-sm" onClick={selectAll}>Select All ({cameras.length})</button>
                  <button className="btn btn-ghost btn-sm" onClick={selectNone}>Deselect All</button>
                  <span className="ra-wizard-camera-count">
                    {selectedCameras.size} of {cameras.length} selected
                    {license && selectedCameras.size > license.maxCameras && (
                      <span className="ra-wizard-over-limit"> (exceeds license limit of {license.maxCameras})</span>
                    )}
                  </span>
                </div>

                <div className="ra-wizard-camera-grid">
                  {cameras.map(cam => (
                    <div
                      key={cam.id}
                      className={`ra-wizard-camera-item ${selectedCameras.has(cam.id) ? 'selected' : ''}`}
                      onClick={() => toggleCamera(cam.id)}
                    >
                      <div className={`ra-wizard-camera-check ${selectedCameras.has(cam.id) ? 'checked' : ''}`}>
                        {selectedCameras.has(cam.id) && <Check size={12} />}
                      </div>
                      <div className="ra-wizard-camera-info">
                        <span className="ra-wizard-camera-name">{cam.name}</span>
                        <span className="ra-wizard-camera-meta">
                          <span className={`ra-wizard-camera-dot ${cam.status}`} />
                          {cam.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Step 4: Configure */}
        {currentStep === 4 && (
          <div className="ra-wizard-panel">
            <h2>Review and Enable</h2>
            <p className="ra-wizard-desc">Recording Assurance will begin monitoring these cameras immediately.</p>

            <div className="ra-wizard-review">
              <div className="ra-wizard-review-item">
                <Server size={18} />
                <div>
                  <strong>VMS</strong>
                  <span>{selectedIntegration?.name} ({selectedIntegration?.type.replace('_VMS', '')})</span>
                </div>
              </div>
              <div className="ra-wizard-review-item">
                <Camera size={18} />
                <div>
                  <strong>Cameras</strong>
                  <span>{selectedCameras.size} cameras selected</span>
                </div>
              </div>
              <div className="ra-wizard-review-item">
                <Activity size={18} />
                <div>
                  <strong>Check Interval</strong>
                  <span>Every 10 minutes (configurable per camera later)</span>
                </div>
              </div>
              <div className="ra-wizard-review-item">
                <GraduationCap size={18} />
                <div>
                  <strong>Learning Period</strong>
                  <span>7 days of baseline data collection. Alerts will be suppressed during learning.</span>
                </div>
              </div>
              <div className="ra-wizard-review-item">
                <AlertTriangle size={18} />
                <div>
                  <strong>Alert Threshold</strong>
                  <span>Alerts when confidence drops below 70% (configurable per camera later)</span>
                </div>
              </div>
            </div>

            <div className="ra-wizard-confirm">
              <button className="btn btn-primary" onClick={enrollCameras} disabled={enrolling}>
                {enrolling ? (
                  <><Loader2 size={16} className="spin" /> Enabling cameras...</>
                ) : (
                  <><ShieldCheck size={16} /> Enable Recording Assurance</>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Step 5: Complete */}
        {currentStep === 5 && (
          <div className="ra-wizard-panel">
            <div className="ra-wizard-complete">
              <div className="ra-wizard-complete-icon">
                <ShieldCheck size={56} />
              </div>
              <h2>Recording Assurance is Active</h2>
              <p>{enrollResult?.enabled || selectedCameras.size} cameras are now being monitored.</p>

              <div className="ra-wizard-complete-timeline">
                <div className="ra-wizard-timeline-item active">
                  <div className="ra-wizard-timeline-dot" />
                  <div>
                    <strong>Now</strong>
                    <span>First verification cycle starting</span>
                  </div>
                </div>
                <div className="ra-wizard-timeline-item">
                  <div className="ra-wizard-timeline-dot" />
                  <div>
                    <strong>Next 10 minutes</strong>
                    <span>All cameras will be checked for the first time</span>
                  </div>
                </div>
                <div className="ra-wizard-timeline-item">
                  <div className="ra-wizard-timeline-dot" />
                  <div>
                    <strong>Day 1-7</strong>
                    <span>Learning mode: building behavioral baselines per camera</span>
                  </div>
                </div>
                <div className="ra-wizard-timeline-item">
                  <div className="ra-wizard-timeline-dot" />
                  <div>
                    <strong>Day 8+</strong>
                    <span>Full monitoring active with intelligent alerts</span>
                  </div>
                </div>
              </div>

              <div className="ra-wizard-complete-actions">
                <button className="btn btn-primary" onClick={() => navigate('/recording-assurance')}>
                  <Monitor size={16} /> Go to Dashboard
                </button>
                <button className="btn btn-secondary" onClick={() => { setCurrentStep(1); setSelectedIntegration(null); setCameras([]); }}>
                  <Server size={16} /> Add Another VMS
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      {currentStep < 5 && (
        <div className="ra-wizard-nav">
          <button className="btn btn-ghost" onClick={currentStep === 0 ? () => navigate('/recording-assurance') : goBack} disabled={false}>
            <ArrowLeft size={16} /> {currentStep === 0 ? 'Back to Dashboard' : 'Back'}
          </button>
          {currentStep < 4 && (
            <button className="btn btn-primary" onClick={goNext} disabled={!canProceed()}>
              {currentStep === 0 ? "Let's Get Started" : 'Continue'} <ArrowRight size={16} />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default RecordingAssuranceWizard;
